import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const ProblemSection = () => {
  const { t } = useTranslation();
  const items = t("problem.items", { returnObjects: true }) as Array<{
    metric: string;
    label: string;
    source?: string;
  }>;
  const painPoints = t("problem.painPoints", { returnObjects: true }) as Array<{
    title: string;
    desc: string;
  }>;

  return (
    <section className="relative py-12 md:py-16 bg-[#0B0F0E] text-white overflow-hidden">
      {/* Subtle grid texture */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />
      {/* Soft radial glow */}
      <div className="absolute top-0 right-0 w-[520px] h-[520px] rounded-full opacity-[0.06] blur-3xl bg-amber-400 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[440px] h-[440px] rounded-full opacity-[0.05] blur-3xl bg-[#2FB46D] pointer-events-none" />

      <div className="container mx-auto px-4 max-w-7xl relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          {/* Left: narrative */}
          <div className="lg:col-span-5">
            <motion.span
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-white/60 mb-3"
            >
              <span className="w-6 h-px bg-white/40" />
              {t("problem.badge")}
            </motion.span>

            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.05 }}
              className="text-3xl md:text-4xl lg:text-[40px] font-semibold tracking-tight leading-[1.08] mb-4"
            >
              {t("problem.title")}
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-[14px] md:text-[15px] text-white/70 leading-relaxed max-w-xl mb-6"
            >
              {t("problem.subtitle")}
            </motion.p>

            {/* Pain-point cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3 gap-3 mt-2">
              {painPoints.map((p, i) => (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.15 + i * 0.05, duration: 0.4 }}
                  className="p-3.5 rounded-lg border border-white/10 bg-white/[0.025] hover:bg-white/[0.05] hover:border-[#2FB46D]/30 transition-all"
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#2FB46D]" />
                    <h3 className="text-[12.5px] font-semibold text-white leading-tight">
                      {p.title}
                    </h3>
                  </div>
                  <p className="text-[11.5px] text-white/60 leading-snug">
                    {p.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Right: compact metric cards in grid */}
          <div className="lg:col-span-7 lg:pl-8 lg:border-l border-white/10">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {items.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08, duration: 0.5 }}
                  className="group flex flex-col p-5 rounded-xl border border-white/10 bg-white/[0.02] hover:border-[#2FB46D]/40 hover:bg-white/[0.04] transition-all"
                >
                  <div className="text-3xl md:text-4xl lg:text-[40px] font-semibold tracking-tight tabular-nums text-white leading-none mb-3">
                    {item.metric}
                  </div>
                  <p className="text-[13px] text-white/75 leading-snug flex-1">
                    {item.label}
                  </p>
                  {item.source && (
                    <p className="mt-3 pt-3 border-t border-white/10 text-[10px] font-mono uppercase tracking-[0.1em] text-white/40 leading-snug">
                      {item.source}
                    </p>
                  )}
                </motion.div>
              ))}
            </div>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="mt-5 text-[11px] text-white/45 leading-relaxed"
            >
              {t("problem.sourcesFootnote")}
            </motion.p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;